import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDirectoryContext } from '../contexts/DirectoryContext';
import { useDirectorySearchContext } from '../contexts/DirectorySearchContext';
import usePostMembers from '../hooks/usePostMembers';
import Stack from '@mui/material/Stack';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { IMember } from "../types/interfaces/IMember";
import { FilterParams } from "../types/classes/FilterParams";
import { DisplayParams } from "../types/classes/DisplayParams";
import { Constants } from '../constants/Constants';
import SortMembers from "../hooks/useSortMembers";
import { DirectoryResource } from '../constants/DirectoryResource'

interface AutocompleteCollectionProps {
    displayParams: DisplayParams;
    onDisplayChange: (displayParams: DisplayParams) => void;
}

// The following Co-Pilot suggestions are intentionally avoided:
// - Avoid Reassigning let Variables: 
//      Reason: State change commits are asynchronous and do not occur quickly enough to correctly manage 
//          filter changes made in the same component. This was also encountered in development of Find A Pediatrician.
// - Memoize Expensive Computations (specifically recalculated "unique" lists):
//      Reason: The unique lists are recalculated on every filter change, and memoization would not provide a performance benefit
//          since the lists are not large enough to warrant it. The lists are recalculated only when the filteredMemberList changes, which is the intended behavior.
// - Loading and Error States (Co-Pilot suggests a loading spinner for the filters dispaly) :
//      Reason: The loading and error states are handled in the DirectoryContext and DirectorySearchContext,
//          and the AutocompleteCollection (filters) component is not responsible for displaying them. The loading spinner is already displayed in the DirectoryContext when the filters are being fetched.



/**
 * AutocompleteCollection Component
 * 
 * This component handles the filter display and filter logic for the member directory results.
 * 
 * Props:
 * - displayParams: An object of type DisplayParams containing the current display parameters.
 * - onDisplayChange: A function to handle the display change event.
 * 
 * Example usage:
 * ```tsx
 * <AutocompleteCollection displayParams={displayParams} onDisplayChange={handleDisplayChange} />
 * ```
 * 
 * @param {DisplayParams} displayParams - The current display parameters.
 * @param {(displayParams: DisplayParams) => void} onDisplayChange - The function to handle the display change event.
 * @returns {JSX.Element} The component displaying the member results with pagination.
 */
const AutoCompleteCollection: React.FC<AutocompleteCollectionProps> = ({ displayParams, onDisplayChange }) => {
    const abortControllerRef = useRef<AbortController | null>(null);
    const { filteredMemberList, filterParams, setFilteredMemberList, setFilterParams, setIsInitialState } = useDirectorySearchContext();

    // This local filter parameters represents the current filters specified by the user, if any.
    let localFilterParams: FilterParams = filterParams as FilterParams;
    // These "option" values from DirectoryContext represent the default and initial values for the filter fields.
    // These values are NOT updated by user choices. These can be used on initial display and filter resets.
    const { countryOptions, stateOptions, cityOptions, zipOptions, languageOptions, specialtyOptions, interestOptions } = useDirectoryContext();

    // This is used to create a unique list of zip codes from the zipOptions.
    // The zipOptions are expected to be in the format "12345-6789". We need a unique list zip codes without plus 4. 
    function getUniqueValidZips(zipOptions: string[]): string[] {
        return Array.from(
            new Set(
                zipOptions
                    .map(zip => zip.split('-')[0].trim())
                    .filter(
                        zip =>
                            // Exclude if all digits and all zeros, regardless of length
                            !(zip.match(/^\d+$/) && /^0+$/.test(zip))
                    )
            )
        );
    }
    // This is used to create a unique list of the zip codes filter from DirectoryContext.
    // We use this value for initial state and any resets.   
    const uniqueZipOptions = getUniqueValidZips(zipOptions ?? []);


    // These "filtered" options are updated based on the filteredMemberList. 
    // They are initially set to the default options from DirectoryContext (on first load and after any reset).
    // The renedered autocomplete fields will use these filtered options to display the available choices.
    const [filteredZipOptions, setFilteredZipOptions] = useState<string[]>(uniqueZipOptions);
    const [filteredCountryOptions, setFilteredCountryOptions] = useState<string[]>(countryOptions);
    const [filteredStateOptions, setFilteredStateOptions] = useState<string[]>(stateOptions);
    const [filteredCityOptions, setFilteredCityOptions] = useState<string[]>(cityOptions);
    const [filteredLanguageOptions, setFilteredLanguageOptions] = useState<string[]>(languageOptions);
    const [filteredSpecialtyOptions, setFilteredSpecialtyOptions] = useState<string[]>(specialtyOptions);
    const [filteredInterestOptions, setFilteredInterestOptions] = useState<string[]>(interestOptions);
    // This is used to track the name input value for the name filter.
    const [nameInput, setNameInput] = useState<string>(localFilterParams.LastName ?? null);
    // These sets are used to collect unique values from the filteredMemberList.
    const uniqueZips = new Set<string>();
    const uniqueCountries = new Set<string>();
    const uniqueStates = new Set<string>();
    const uniqueCities = new Set<string>();
    const uniqueLanguagess = new Set<string>();
    const uniqueSpecialties = new Set<string>();
    const uniqueInterests = new Set<string>();

    // This hook is responsible for fetching the members based on the filter parameters.
    const fetchMembers = usePostMembers();

    // this useEffect is responsible for handling the name input changes
    useEffect(() => {
        if (!nameInput) return;
        // if user is starting a new search using name, don't trigger the filter/fetch until 3 characters are added to input field.
        if ((!filteredMemberList || filteredMemberList.length === 0) && displayParams.IsReset) {
            if (nameInput.length >= 3) {
                FilterHandler(Constants.NAME, nameInput);
            }
        }
        // if initial fetch has already occurred (filteredMemberList is populated), match name on each character typed.
        else {
            FilterHandler(Constants.NAME, nameInput);
        }
    }, [nameInput]);

    // this useEffect is responsible for updating selection options for the filter fields
    // all filter fields are updated when the filteredMemberList is updated in method CreateFilter()
    useEffect(() => {

        if ((!filteredMemberList || filteredMemberList.length === 0) && displayParams.IsReset) {
            // if the filteredMemberList is empty, we reset the filtered options to the default options from DirectoryContext.
            // filteredMemberList will be empty on first load and after any reset.
            setFilteredStateOptions(Array.from(stateOptions ?? []).sort());
            setFilteredCityOptions(Array.from(cityOptions ?? []).sort());
            setFilteredCountryOptions(Array.from(countryOptions ?? []).sort());
            setFilteredZipOptions(Array.from(uniqueZipOptions ?? []).sort());
            setFilteredLanguageOptions(Array.from(languageOptions ?? []).sort());
            setFilteredSpecialtyOptions(Array.from(specialtyOptions ?? []).sort());
            setFilteredInterestOptions(Array.from(interestOptions ?? []).sort());
        }
        else {

            filteredMemberList?.forEach((member: IMember) => {

                member.Addresses?.forEach(address => {
                    uniqueStates.add(address.State?.trim().toUpperCase() || '');
                    uniqueCities.add(address.City?.trim().toUpperCase() || '');
                    uniqueCountries.add(address.Country?.trim().toUpperCase() || '');
                    const zip = address.Zip?.trim().toUpperCase() || '';
                    if (zip !== 'NO ZIP' && zip !== 'NOT AVAILABLE' && !zip.includes('BOX')) {
                        uniqueZips.add(zip);
                    }
                });
                member.Languages?.split(',').forEach(language => uniqueLanguagess.add(language.trim().toUpperCase()));
                member.Specialties?.split(',').forEach(specialty => uniqueSpecialties.add(specialty.trim().toUpperCase()));
                member.Interests?.split(',').forEach(interest => uniqueInterests.add(interest.trim().toUpperCase()));
            });

            // Create unique sorted lists on zips without plus 4.
            const uniqueFilteredZipOptions = getUniqueValidZips(Array.from(uniqueZips)).sort();

            setFilteredStateOptions(Array.from(uniqueStates).sort());
            setFilteredCityOptions(Array.from(uniqueCities).sort());
            setFilteredCountryOptions(Array.from(uniqueCountries).sort());
            setFilteredZipOptions(uniqueFilteredZipOptions);
            setFilteredLanguageOptions(Array.from(uniqueLanguagess).sort());
            setFilteredSpecialtyOptions(Array.from(uniqueSpecialties).sort());
            setFilteredInterestOptions(Array.from(uniqueInterests).sort());
        }

    }, [filteredMemberList]);

    // this method resets all filter parameters and renews the filtered list
    const ResetFilters = useCallback(() => {
        ResetFilterParams();
        ResetDisplayParams();
        setIsInitialState(true);
        setFilteredMemberList([]);
        setNameInput('');
        setFilteredZipOptions(uniqueZipOptions);
        setFilteredCountryOptions(countryOptions);
        setFilteredStateOptions(stateOptions);
        setFilteredCityOptions(cityOptions);
        setFilteredLanguageOptions(languageOptions);
        setFilteredSpecialtyOptions(specialtyOptions);
        setFilteredInterestOptions(interestOptions);
    }, [filterParams, setFilterParams, setFilteredMemberList, stateOptions, cityOptions]);

    // This resets all search filter parameters
    const ResetFilterParams = useCallback(() => {
        setFilterParams(new FilterParams());
    }, [filterParams]);

    // This resets all display parameters including the page number and the card display expansion.
    const ResetDisplayParams = useCallback(() => {
        let newDisplayParams = new DisplayParams();
        newDisplayParams.Page = 1;
        newDisplayParams.ShowDetailFlag = true; // this is counter-intuitive. this actually hides the opened detail view on reset.
        newDisplayParams.IsReset = true; // this is used to indicate that the filters have been reset and the member list is empty.
        onDisplayChange(newDisplayParams);
    }, [displayParams]);

    // this method checks if the value is not an empty string
    const isNotEmpty = (value: string): boolean => value.trim() !== "";

    // this method checks if the selected filter field is the only non-null string property in the filter parameters
    // when this returns true, we allow the user to apply the same filter (with possibly diferrent value) to an unfiltered list
    // when this returns false, we only apply the filter to the already filtered list
    // Note: this method only considers string properties and ignores the IsReset boolean in FilterParams.
    // Use case example: User initially selects state IL, then changes the state to CA.
    // In this case, we want to apply the filter to the unfiltered list of members. 
    const isOnlyNonNullProperty = (params: FilterParams, propertyName: string): boolean => {
        return Object.keys(params).every(key => {
            if (key === propertyName) return true;
            const value = params[key as keyof FilterParams];
            if (typeof value !== 'string') return true; // Ignore non-string properties
            return value === null || value.trim() === '';
        });
    };

    // this method applies the selected filter to the list of members.
    // if only one filter is being applied (or re-applied), we apply the filter to the unfiltered list
    // if more than one filter is specified in localFilterParams (see: isOnlyNonNullProperty()), we apply the filter to the already filtered list
    const CreateFilter = (filterName: string): void => {

        const filterByField = (field: string | null | undefined, extractor: (member: IMember) => string[]): void => {
            if (field != null && isNotEmpty(field)) {
                if (filterName === Constants.NAME || filterName === Constants.ZIPCODE) {
                    // substring match on name
                    currentFilteredMembers = currentFilteredMembers.filter(member =>
                        extractor(member).some(value => value.toLowerCase().includes(field.trim().toLowerCase()))
                    );
                }
                else {
                    // match exact on all other fields
                    currentFilteredMembers = currentFilteredMembers.filter(member =>
                        extractor(member).some(value => value.trim().toLowerCase() === field.trim().toLowerCase())
                    );
                }
            }
        };

        const filters: { [key: string]: string | null | undefined } = {
            [Constants.COUNTRY]: localFilterParams.Country,
            [Constants.STATE]: localFilterParams.State,
            [Constants.CITY]: localFilterParams.City,
            [Constants.ZIPCODE]: localFilterParams.ZipCode,
            [Constants.LANGUAGE]: localFilterParams.Language,
            [Constants.SPECIALTY]: localFilterParams.Specialty,
            [Constants.INTEREST]: localFilterParams.Interest,
            [Constants.NAME]: localFilterParams.LastName
        };

        const extractors: { [key: string]: (member: IMember) => string[] } = {
            [Constants.COUNTRY]: (member: IMember) => member.Addresses?.flatMap(address => address.Country ? [address.Country] : []) ?? [],
            [Constants.STATE]: (member: IMember) => member.Addresses?.flatMap(address => address.State ? [address.State] : []) ?? [],
            [Constants.CITY]: (member: IMember) => member.Addresses?.flatMap(address => address.City ? [address.City] : []) ?? [],
            [Constants.ZIPCODE]: (member: IMember) => member.Addresses?.flatMap(address => address.Zip ? [address.Zip] : []) ?? [],
            [Constants.LANGUAGE]: (member: IMember) => member.Languages ? member.Languages.split(',').map(lang => lang.trim()) : [],
            [Constants.SPECIALTY]: (member: IMember) => member.Specialties ? member.Specialties.split(',').map(spec => spec.trim()) : [],
            [Constants.INTEREST]: (member: IMember) => member.Interests ? member.Interests.split(',').map(interest => interest.trim()) : [],
            [Constants.NAME]: (member: IMember) => member.LastName ? [member.LastName] : []
        };

        let currentFilteredMembers: IMember[] = [];

        if (isOnlyNonNullProperty(localFilterParams, filterName)) {
            // Abort any previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            const controller = new AbortController();
            abortControllerRef.current = controller;


            // if the filterName is the only non-null property in localFilterParams, it indicates a new search.
            // We apply the filter to a new member fetch.
            fetchMembers(localFilterParams, controller.signal).then(members => {
                if (!controller.signal.aborted && members) {
                    // update the currentFilteredMembers with the fetched members
                    currentFilteredMembers = members as IMember[] || [];
                    setFilteredMemberList(SortMembers(currentFilteredMembers));
                    // this is to indicate that we have attempted to populate the member list.
                    // This enables correct messaging in ItemCollectionDisplay if subsequently applied filters do not return any members.
                    const newDisplayParams = { ...displayParams, IsReset: false };
                    onDisplayChange(newDisplayParams);
                }
            });


        }
        else {
            currentFilteredMembers = filteredMemberList as IMember[] || [];
            if (filters[filterName] !== undefined && extractors[filterName] !== undefined) {
                filterByField(filters[filterName], extractors[filterName]);
            }
        }
        setFilteredMemberList(SortMembers(currentFilteredMembers));
        ResetDisplayParams(); //resets selected pagination number and closes any detail view
    };

    React.useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // this method handles the filter parameters based on the selected filter field
    // this is implemented each time the user selects from a filter field
    const FilterHandler = (addedFilterType: string, addedFilterValue: string | null): void => {

        const applyToFilter = (): void => {
            setIsInitialState(false);
            setFilterParams(localFilterParams);
            CreateFilter(addedFilterType);
        };

        switch (addedFilterType) {
            case Constants.COUNTRY:
                localFilterParams.Country = addedFilterValue?.trim() || '';
                break;
            case Constants.STATE:
                localFilterParams.State = addedFilterValue?.trim() || '';
                break;
            case Constants.CITY:
                localFilterParams.City = addedFilterValue?.trim() || '';
                break;
            case Constants.ZIPCODE:
                localFilterParams.ZipCode = addedFilterValue?.trim() || '';
                break;
            case Constants.LANGUAGE:
                localFilterParams.Language = addedFilterValue?.trim() || '';
                break;
            case Constants.SPECIALTY:
                localFilterParams.Specialty = addedFilterValue?.trim() || '';
                break;
            case Constants.INTEREST:
                localFilterParams.Interest = addedFilterValue?.trim() || '';
                break;
            case Constants.NAME:
                localFilterParams.LastName = addedFilterValue?.trim() || '';
                break;
            default:
                break;
        }
        applyToFilter();
    };

    const filterConfigs = [
        { id: "zip_autocomplete", label: DirectoryResource.zip_lbl, value: localFilterParams.ZipCode, options: filteredZipOptions, constant: Constants.ZIPCODE },
        { id: "country_autocomplete", label: DirectoryResource.country_lbl, value: localFilterParams.Country, options: filteredCountryOptions, constant: Constants.COUNTRY },
        { id: "state_autocomplete", label: DirectoryResource.state_lbl, value: localFilterParams.State, options: filteredStateOptions, constant: Constants.STATE },
        { id: "city_autocomplete", label: DirectoryResource.city_lbl, value: localFilterParams.City, options: filteredCityOptions, constant: Constants.CITY },
        { id: "language_autocomplete", label: DirectoryResource.languages_lbl, value: localFilterParams.Language, options: filteredLanguageOptions, constant: Constants.LANGUAGE },
        { id: "specialty_autocomplete", label: DirectoryResource.specialty_lbl, value: localFilterParams.Specialty, options: filteredSpecialtyOptions, constant: Constants.SPECIALTY },
        { id: "interest_autocomplete", label: DirectoryResource.interest_lbl, value: localFilterParams.Interest, options: filteredInterestOptions, constant: Constants.INTEREST }

    ];

    return (
        <Stack spacing={0} sx={{ width: 272 }}>
            <Stack spacing={15} direction="row">
                <Typography  variant="h4" color="primary.main" component="div" sx={{ ml: 0 }}>{DirectoryResource.filter_lbl}</Typography>
                <Link color="secondary.dark" sx={{fontSize: 18}} component="button" onClick={ResetFilters} >{DirectoryResource.reset_lbl}</Link>
            </Stack>
            <TextField
                id="name_textbox"
                label={DirectoryResource.name_lbl}
                type="search"
                value={nameInput}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNameInput(event.target.value)}
                sx={{ width: '90%', mb:2 }}
            />
            {filterConfigs.map(cfg => (
                <Autocomplete
                    key={cfg.id}
                    id={cfg.id}
                    options={cfg.options}
                    value={cfg.value ?? null}
                    onChange={(_event, newValue) => { FilterHandler(cfg.constant, newValue); }}
                    isOptionEqualToValue={(option, value) => option == value}
                    renderInput={(params) => <TextField {...params} label={cfg.label} />}
                    disableClearable
                    sx={{ width: '90%' , mb:2}}
                />
            ))}
        </Stack>
    );
}

export default AutoCompleteCollection;